/**
 * FastStudioShell - Main Container Component
 *
 * The unified hub that contains all FAST Studio workspaces.
 * Manages navigation, workspace state, and AI assistant dock.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Tooltip, IconButton } from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import AuthService from '../../services/AuthService';

import Sidebar from './Sidebar';
import StatusBar from './StatusBar';
import AiAssistantDock from './AiAssistantDock';
import WelcomeDashboard from './WelcomeDashboard';

// Workspace components (lazy loaded for performance)
import LlmLabWorkspace from './workspaces/LlmLabWorkspace';
import FlexWorkspace from './workspaces/FlexWorkspace';
import ApiHoundWorkspace from './workspaces/ApiHoundWorkspace';
import WorkflowBuilderWorkspace from './workspaces/WorkflowBuilderWorkspace';
import IntegrationWorkspace from './workspaces/IntegrationWorkspace';
import TasksWorkspace from './workspaces/TasksWorkspace';
import WorkspacesManagementWorkspace from './workspaces/WorkspacesManagementWorkspace';

import {
  WORKSPACE_IDS,
  WORKSPACES,
  SIDEBAR_WIDTH,
  AI_DOCK_WIDTH,
  STATUS_BAR_HEIGHT,
} from './constants';

/**
 * FastStudioShell Component
 */
const FastStudioShell = () => {
  const { colors } = useAppTheme();
  const { user, userRoles, canAccessWorkspace, logout } = useAuth();

  // Deep-link params -- e.g. /studio?workspace=flex&scriptId=<id>, used by
  // FAST_CONFIG's FLEX Commands tab's Edit action (OPEN_URL, see
  // actionHandlerRegistry.js) to jump straight into the real IDE with that
  // command pre-loaded, instead of a generic metadata form. Read once on
  // mount -- /studio is its own standalone route (opened in a new tab, see
  // the "Open FAST Studio" button elsewhere in the app), not a route that's
  // re-navigated to with new params while already mounted.
  // See project_flex_commands_rename_and_studio_edit_link.
  const deepLinkParams = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return { workspace: params.get('workspace'), scriptId: params.get('scriptId') };
    } catch {
      return { workspace: null, scriptId: null };
    }
  }, []);

  // Check the same explicit developer authority enforced by the Studio APIs; errors deny access.
  const [studioAllowed, setStudioAllowed] = useState(null);
  useEffect(() => {
    let cancelled = false;
    AuthService.checkStudioAccess().then((allowed) => {
      if (!cancelled) setStudioAllowed(allowed);
    });
    return () => { cancelled = true; };
  }, []);

  // Sidebar state
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  // Active workspace — restored from the last session so a page reload
  // doesn't dump the user back on Home. Validated against known workspace
  // ids here; role-based access is re-checked in the effect below once
  // canAccessWorkspace is available (a stale/inaccessible saved id falls
  // back to Home rather than rendering something the current user can't see).
  // A ?workspace= deep-link param wins over the restored session, so an
  // Edit-action link always lands where it says it will.
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(() => {
    if (deepLinkParams.workspace) {
      const match = Object.values(WORKSPACE_IDS).find(
        (id) => id === deepLinkParams.workspace || id.toLowerCase() === deepLinkParams.workspace.toLowerCase()
      );
      if (match && WORKSPACES[match]) return match;
    }
    try {
      const saved = localStorage.getItem('fastStudio.activeWorkspaceId');
      if (saved && WORKSPACES[saved]) return saved;
    } catch {
      // localStorage unavailable (private mode, disabled) — fall through to default
    }
    return WORKSPACE_IDS.HOME;
  });

  // AI Assistant dock state
  const [aiDockOpen, setAiDockOpen] = useState(true);
  const [aiDockWidth, setAiDockWidth] = useState(AI_DOCK_WIDTH.default);

  // Get current workspace config
  const activeWorkspace = WORKSPACES[activeWorkspaceId];

  // Filter workspaces by user role
  const accessibleWorkspaces = useMemo(() => {
    return Object.values(WORKSPACES).filter(ws => canAccessWorkspace(ws.allowedRoles));
  }, [canAccessWorkspace]);

  // If the restored workspace isn't accessible to this user (role changed,
  // or the saved id belongs to a different account), fall back to Home.
  useEffect(() => {
    const workspace = WORKSPACES[activeWorkspaceId];
    if (workspace && !canAccessWorkspace(workspace.allowedRoles)) {
      setActiveWorkspaceId(WORKSPACE_IDS.HOME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessWorkspace]);

  // Persist the active workspace so a reload lands back where the user left off.
  useEffect(() => {
    try {
      localStorage.setItem('fastStudio.activeWorkspaceId', activeWorkspaceId);
    } catch {
      // localStorage unavailable — persistence is best-effort, not required
    }
  }, [activeWorkspaceId]);

  // Handle workspace change
  const handleWorkspaceChange = useCallback((workspaceId) => {
    const workspace = WORKSPACES[workspaceId];
    if (workspace && canAccessWorkspace(workspace.allowedRoles)) {
      setActiveWorkspaceId(workspaceId);
      // Collapse Atom panel when opening a workspace (not Home)
      if (workspaceId !== WORKSPACE_IDS.HOME) {
        setAiDockOpen(false);
      }
    }
  }, [canAccessWorkspace]);

  // Handle sidebar toggle
  const handleSidebarToggle = useCallback(() => {
    setSidebarExpanded(prev => !prev);
  }, []);

  // Handle AI dock toggle
  const handleAiDockToggle = useCallback(() => {
    setAiDockOpen(prev => !prev);
  }, []);

  // Handle AI dock resize
  const handleAiDockResize = useCallback((newWidth) => {
    const clampedWidth = Math.max(AI_DOCK_WIDTH.min, Math.min(AI_DOCK_WIDTH.max, newWidth));
    setAiDockWidth(clampedWidth);
  }, []);

  // Calculate layout dimensions
  const sidebarWidth = sidebarExpanded ? SIDEBAR_WIDTH.expanded : SIDEBAR_WIDTH.collapsed;
  const aiDockActualWidth = aiDockOpen ? aiDockWidth : 0;
  const contentWidth = `calc(100% - ${sidebarWidth}px - ${aiDockActualWidth}px)`;

  // Workspace visibility style helper - keeps all workspaces mounted to preserve state
  const getWorkspaceStyle = (workspaceId) => ({
    display: activeWorkspaceId === workspaceId ? 'flex' : 'none',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
  });

  if (studioAllowed === null) {
    // Still checking server-side -- render nothing rather than a flash of real
    // Studio content for a tenant about to be denied.
    return <Box sx={{ height: '100vh', bgcolor: colors.background }} />;
  }

  if (studioAllowed === false) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: colors.background,
          color: colors.textPrimary,
          gap: 2,
          textAlign: 'center',
          px: 3,
        }}
      >
        <Box sx={{ fontSize: 20, fontWeight: 600 }}>FAST Studio access is not available for this account</Box>
        <Box sx={{ color: colors.textSecondary, maxWidth: 420 }}>
          An authorized developer account with explicit platform grants is required. Close this tab to return to FAST.
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: colors.background,
        overflow: 'hidden',
      }}
    >
      {/* Logout -- FastStudioShell has no top AppBar/Toolbar of its own (unlike
          AtomApp.js), so this is a fixed overlay rather than sitting inside an
          existing header bar. Same icon/behavior as the main app's logout
          button (LogoutIcon + useAuth().logout()). */}
      <Tooltip title={user ? `Logout ${user.displayName || user.username}` : 'Logout'}>
        <IconButton
          onClick={logout}
          aria-label="Logout"
          sx={{
            position: 'fixed',
            top: 8,
            right: 8,
            zIndex: (theme) => theme.zIndex.drawer + 10,
            color: colors.text?.secondary || colors.textSecondary,
            bgcolor: colors.surface,
            border: `1px solid ${colors.border?.default || colors.border}`,
            '&:hover': { color: colors.error?.main || colors.error, bgcolor: colors.surfaceElevated },
          }}
        >
          <LogoutIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Sidebar
          colors={colors}
          expanded={sidebarExpanded}
          onToggle={handleSidebarToggle}
          activeWorkspaceId={activeWorkspaceId}
          onWorkspaceChange={handleWorkspaceChange}
          accessibleWorkspaces={accessibleWorkspaces}
        />

        {/* Workspace Content - All workspaces rendered but only active one visible */}
        <Box
          sx={{
            width: contentWidth,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'width 0.2s ease',
            position: 'relative',
          }}
        >
          {/* Home/Welcome Dashboard */}
          <Box sx={getWorkspaceStyle(WORKSPACE_IDS.HOME)}>
            <WelcomeDashboard
              colors={colors}
              onNavigate={handleWorkspaceChange}
              accessibleWorkspaces={accessibleWorkspaces}
            />
          </Box>

          {/* LLM Lab */}
          <Box sx={getWorkspaceStyle(WORKSPACE_IDS.LLM_LAB)}>
            <LlmLabWorkspace colors={colors} />
          </Box>

          {/* Flex */}
          <Box sx={getWorkspaceStyle(WORKSPACE_IDS.FLEX)}>
            <FlexWorkspace colors={colors} initialScriptId={deepLinkParams.scriptId} />
          </Box>

          {/* API Hound */}
          <Box sx={getWorkspaceStyle(WORKSPACE_IDS.API_HOUND)}>
            <ApiHoundWorkspace colors={colors} />
          </Box>

          {/* Workflow Builder */}
          <Box sx={getWorkspaceStyle(WORKSPACE_IDS.WORKFLOW_BUILDER)}>
            <WorkflowBuilderWorkspace colors={colors} />
          </Box>

          {/* Integration */}
          <Box sx={getWorkspaceStyle(WORKSPACE_IDS.INTEGRATION)}>
            <IntegrationWorkspace colors={colors} />
          </Box>

          {/* Tasks */}
          <Box sx={getWorkspaceStyle(WORKSPACE_IDS.TASKS)}>
            <TasksWorkspace colors={colors} />
          </Box>

          {/* Workspaces Management */}
          <Box sx={getWorkspaceStyle(WORKSPACE_IDS.WORKSPACES_MGMT)}>
            <WorkspacesManagementWorkspace colors={colors} />
          </Box>
        </Box>

        {/* AI Assistant Edge Toggle */}
        <Box
          onClick={handleAiDockToggle}
          sx={{
            width: 16,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            bgcolor: colors.surfaceElevated,
            borderLeft: `1px solid ${colors.border}`,
            '&:hover': { bgcolor: colors.surfaceHover },
            transition: 'background-color 0.2s',
          }}
        >
          <Tooltip title={aiDockOpen ? 'Collapse AI Assistant' : 'Expand AI Assistant'} placement="left">
            {aiDockOpen
              ? <ChevronRightIcon sx={{ fontSize: 14, color: colors.textMuted }} />
              : <ChevronLeftIcon sx={{ fontSize: 14, color: colors.textMuted }} />
            }
          </Tooltip>
        </Box>

        {/* AI Assistant Dock */}
        {aiDockOpen && (
        <AiAssistantDock
          colors={colors}
          open={aiDockOpen}
          width={aiDockWidth}
          onToggle={handleAiDockToggle}
          onResize={handleAiDockResize}
          context={activeWorkspace?.aiContext || 'general'}
          workspaceName={activeWorkspace?.label || 'FAST Studio'}
        />
        )}
      </Box>

      {/* Status Bar */}
      <StatusBar
        colors={colors}
        height={STATUS_BAR_HEIGHT}
        activeWorkspace={activeWorkspace}
        tabCount={0}
        userRoles={userRoles}
        aiDockOpen={aiDockOpen}
        onAiDockToggle={handleAiDockToggle}
      />
    </Box>
  );
};

export default FastStudioShell;
